// Header alias dictionary + AssetPro import-template definition for Smart Asset Import.
//
// This is the "smart mapping" knowledge, expressed as data rather than AI. Each canonical
// field lists the header spellings we have actually seen (seeded from the two real Chabou
// & Co. files + the AssetPro template). Matching is EXACT on the normalized key
// (see textNormalize.normalizeKey) — an alias either matches or it does not. Unknown
// headers are NOT guessed; they go to the review screen where the user maps them once,
// and that choice is remembered (learnedAliases) so the same header auto-maps next time.

import { normalizeKey } from './textNormalize';

// ---------------------------------------------------------------------------
// AssetPro import template — EXACT output column headers (Sample Asset Import File v2.xlsx).
// The whitespace quirks are intentional and must be preserved for the importer:
//   col 7  = ' Location'  (leading space)
//   col 18 = ' QTY '      (leading + trailing space)
// ---------------------------------------------------------------------------
export const OUTPUT_COLUMNS = [
  'Sn No',
  'Asset Type',
  'Asset Sub Type',
  'Model',
  'Make',
  'Description',
  ' Location',
  'Location Code',
  'Sub Location',
  'Sub Location Code',
  'Owner',
  'Purchase Date',
  'Purchase Vendor',
  'Purchase Invoice No',
  'Purchase Amount',
  'Asset Subtype Code',
  'Owner Code',
  ' QTY ',
];

// Canonical internal field -> { outputColumn, required, autoGenerated, masterKind }
// masterKind drives exact-match validation against AssetPro master data (referenceResolver).
export const FIELD_DEFS = {
  snNo: { outputColumn: 'Sn No', required: true, autoGenerated: false },
  assetType: { outputColumn: 'Asset Type', required: true, masterKind: 'assetType' },
  assetSubType: { outputColumn: 'Asset Sub Type', required: true, masterKind: 'assetSubType' },
  // Model is NOT mapped from source — AssetPro auto-creates it as
  // Make + Asset Sub Type + Model No (optional). Treated like the generated code columns.
  model: { outputColumn: 'Model', required: true, autoGenerated: true, derived: 'modelName' },
  make: { outputColumn: 'Make', required: true, masterKind: 'make' },
  description: { outputColumn: 'Description', required: false },
  location: { outputColumn: ' Location', required: true, masterKind: 'location' },
  locationCode: { outputColumn: 'Location Code', required: true, autoGenerated: true, from: 'location' },
  // Sub Location (and its code) are OPTIONAL. When a Sub Location is present its 4-letter code is
  // auto-generated (generateLocationCode); when absent, both stay blank and are not flagged.
  subLocation: { outputColumn: 'Sub Location', required: false },
  subLocationCode: { outputColumn: 'Sub Location Code', required: false, autoGenerated: true, from: 'subLocation' },
  owner: { outputColumn: 'Owner', required: false, masterKind: 'owner' },
  // Purchase Date is required — it is the asset's creation date in AssetPro. Purchase Vendor,
  // Purchase Invoice No and Owner Code are also required (typically supplied via bulk fill).
  purchaseDate: { outputColumn: 'Purchase Date', required: true, isDate: true },
  purchaseVendor: { outputColumn: 'Purchase Vendor', required: true },
  purchaseInvoiceNo: { outputColumn: 'Purchase Invoice No', required: true },
  purchaseAmount: { outputColumn: 'Purchase Amount', required: false },
  // Asset Subtype Code is mappable from the file (e.g. a "Sub Code" column) and falls back to
  // being generated from the Asset Sub Type name when no source value is present.
  assetSubtypeCode: { outputColumn: 'Asset Subtype Code', required: true, fallbackFrom: 'assetSubType' },
  ownerCode: { outputColumn: 'Owner Code', required: true },
  qty: { outputColumn: ' QTY ', required: true, isQty: true },
};

// Business-critical fields that must EXACT-match AssetPro master data for a row to
// be eligible for auto-accept (strict mode). Missing/unmatched -> review.
// Model is intentionally NOT here: it is auto-created (Make + Asset Sub Type + Model No),
// so it is expected to be new and is not gated on existing master data.
export const MASTER_CRITICAL_FIELDS = ['assetType', 'assetSubType', 'make', 'location'];

// Header alias dictionary. Keys are canonical fields; values are header spellings.
// Also includes source-only fields (altName, serial, etc.) that are NOT template
// columns but that we recognize so they can be surfaced rather than silently dropped.
export const ALIASES = {
  snNo: ['sn no', 'sl no', 'serial no', 'serial number', 's no', 'sr no', 'mac'],
  // 'category' (asset classification) and bare 'type' are intentionally NOT aliases — they are
  // a different concept from Asset Type and were mis-mapping. The first alias is the canonical
  // one and wins any conflict (see buildAliasIndex rank + columnMapper priority).
  assetType: ['asset type', 'machine type', 'equipment type'],
  assetSubType: ['asset sub type', 'sub asset', 'sub type', 'subtype', 'sub asset type'],
  // Model No is an OPTIONAL input to the Asset Model formula (Make + Sub Type + Model No),
  // not the model name itself. There is deliberately no `model` alias — the Model column is
  // auto-created, so a source "Model" / "MODEL/KVA" free-text column stays unmapped (and is
  // surfaced to the user) rather than being silently treated as the asset model.
  modelNo: ['model no', 'model number', 'modelno', 'model code', 'mfg model no'],
  make: ['make', 'company name', 'manufacturer', 'brand', 'company'],
  description: ['description', 'desc', 'asset description', 'particulars'],
  location: ['location', 'current location', 'site location', 'site', 'place', 'deployment location'],
  subLocation: ['sub location', 'sublocation', 'floor', 'area', 'section'],
  owner: ['owner', 'owner name', 'custodian', 'owned by'],
  purchaseDate: ['purchase date', 'date of manufacture', 'mfg date', 'manufacture date', 'date of purchase', 'dop'],
  purchaseVendor: ['purchase vendor', 'vendor', 'supplier', 'dealer'],
  purchaseInvoiceNo: ['purchase invoice no', 'invoice no', 'invoice number', 'bill no'],
  purchaseAmount: ['purchase amount', 'amount', 'cost', 'value', 'purchase value', 'price'],
  ownerCode: ['owner code', 'owner code,if any', 'owner code if any', 'old code', 'existing code'],
  // Asset Subtype Code: the "Sub Code" column in client files (BHL, DIG, …). Mappable; if
  // absent it is generated from the Asset Sub Type name (generateAssetSubtypeCode).
  assetSubtypeCode: ['asset subtype code', 'asset sub type code', 'subtype code', 'sub code', 'subcode', 'sub asset code'],
  qty: ['qty', 'quantity', 'count', 'nos', 'no of units', 'units', 'number'],

  // --- source-only HELPER fields: not output columns, but used for a calculation ---
  // (duplicate detection uses reg/chassis/existing-code; Remark is a Description fallback).
  // We deliberately do NOT alias purely-decorative columns like Status / Sub Code / Category /
  // Capacity — they are neither exported nor used in a calculation, so they stay unmapped
  // rather than cluttering the mapping screen.
  altName: ['veh reg no', 'vehicle reg no', 'registration', 'registration no', 'reg no', 'alt name', 'vehicle no', 'reg number'],
  serial: ['machine no/chasis no', 'chassis no', 'chasis no', 'serial no', 'engine no', 'machine no'],
  existingAssetCode: ['existing asset code', 'existing asset code as per assetpro', 'asset code', 'assetpro code'],
  remark: ['remark', 'remarks', 'note', 'notes', 'comment', 'comments'],
};

// Source-only fields: recognized for a calculation but never written to the import template.
export const SOURCE_ONLY_FIELDS = ['altName', 'serial', 'existingAssetCode', 'remark', 'modelNo'];

// Fields that matter to the OUTPUT (template columns mapped from source) plus calculation
// inputs that produce an output column (modelNo builds Model). These are shown in the main
// mapping table; the source-only helpers above are shown separately as minor/optional.
export const OUTPUT_RELEVANT_FIELDS = [
  ...Object.keys(FIELD_DEFS).filter((fk) => !FIELD_DEFS[fk].autoGenerated),
  'modelNo',
];

/**
 * Build a reverse lookup: normalized alias -> canonical field key.
 * Includes both the built-in ALIASES and any learned (user-confirmed) aliases.
 * Earlier entries win; built-ins are added first.
 *
 * @param {Object<string,string>} learnedAliases  map of normalizedHeader -> fieldKey
 */
export const buildAliasIndex = (learnedAliases = {}) => {
  const index = {};
  Object.keys(ALIASES).forEach((fieldKey) => {
    ALIASES[fieldKey].forEach((alias, rank) => {
      const key = normalizeKey(alias);
      // rank = position in the alias list; the canonical (first) alias has rank 0 and wins
      // conflicts when two source columns map to the same field.
      if (key && index[key] === undefined) index[key] = { field: fieldKey, rank };
    });
  });
  // Learned (user-confirmed) aliases have the highest priority.
  Object.keys(learnedAliases || {}).forEach((normHeader) => {
    if (normHeader) index[normHeader] = { field: learnedAliases[normHeader], rank: -1 };
  });
  return index;
};
